import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2, Lock } from 'lucide-react';
import { useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { ApiError } from '@/shared/api/api-error';
import { fromLocalInput, toLocalInput } from '@/shared/lib/datetime-local';

import { useCreateGlobalDiscount, useUpdateGlobalDiscount } from '../api/global-discount.queries';
import {
  globalDiscountSchema,
  type GlobalDiscountFormValues,
} from '../schemas/global-discount.schema';
import type { GlobalDiscount } from '../types/global-discount.types';
import { BannerPreview } from './banner-preview';

const FIELDS = [
  'percentOff',
  'maxDiscountAmount',
  'startsAt',
  'endsAt',
  'bannerTitleEn',
  'bannerTitleAr',
  'bannerTextEn',
  'bannerTextAr',
  'description',
] as const;

function defaults(discount: GlobalDiscount | undefined): GlobalDiscountFormValues {
  const now = new Date();
  const inAWeek = new Date(now.getTime() + 7 * 24 * 3600 * 1000);
  return {
    percentOff: discount?.percentOff ?? 10,
    maxDiscountAmount: discount?.maxDiscountAmount ?? null,
    startsAt: toLocalInput(discount?.startsAt ?? now.toISOString()),
    endsAt: toLocalInput(discount?.endsAt ?? inAWeek.toISOString()),
    bannerEnabled: discount?.bannerEnabled ?? true,
    bannerTitleEn: discount?.bannerTitleEn ?? '',
    bannerTitleAr: discount?.bannerTitleAr ?? '',
    bannerTextEn: discount?.bannerTextEn ?? '',
    bannerTextAr: discount?.bannerTextAr ?? '',
    description: discount?.description ?? '',
  };
}

function numberValue(value: number | null) {
  return value === null || Number.isNaN(value) ? '' : value;
}

const blankToNull = (value: string) => (value.trim() === '' ? null : value.trim());

/**
 * Create a global discount or change one. Only one sale per country per period: the backend refuses
 * an overlapping period (GLOBAL_DISCOUNT_OVERLAP). Once orders used a sale, its percent and cap are
 * locked; the dates and banner can still change.
 */
export function GlobalDiscountDialog({
  countryId,
  countryName,
  currency,
  discount,
  onClose,
}: {
  countryId: number;
  countryName: string;
  currency: string | null;
  discount?: GlobalDiscount;
  onClose: () => void;
}) {
  const create = useCreateGlobalDiscount(countryId);
  const update = useUpdateGlobalDiscount();
  const mutation = discount ? update : create;
  const locked = discount?.locked ?? false;
  const form = useForm<GlobalDiscountFormValues>({
    resolver: zodResolver(globalDiscountSchema),
    defaultValues: defaults(discount),
  });
  const preview = useWatch({ control: form.control });

  const { error } = mutation;
  useEffect(() => {
    if (!(error instanceof ApiError) || !error.fieldErrors) return;
    for (const [field, message] of Object.entries(error.fieldErrors)) {
      const key = FIELDS.find((name) => name.toLowerCase() === field.toLowerCase());
      if (key) form.setError(key, { type: 'server', message });
    }
  }, [error, form]);
  const formError = error instanceof ApiError && !error.fieldErrors ? error.message : null;

  const submit = (values: GlobalDiscountFormValues) => {
    const input = {
      percentOff: values.percentOff,
      maxDiscountAmount: values.maxDiscountAmount,
      startsAt: fromLocalInput(values.startsAt),
      endsAt: fromLocalInput(values.endsAt),
      bannerEnabled: values.bannerEnabled,
      bannerTitleEn: blankToNull(values.bannerTitleEn),
      bannerTitleAr: blankToNull(values.bannerTitleAr),
      bannerTextEn: blankToNull(values.bannerTextEn),
      bannerTextAr: blankToNull(values.bannerTextAr),
      description: blankToNull(values.description),
    };
    if (discount) update.mutate({ id: discount.id, values: input }, { onSuccess: onClose });
    else create.mutate(input, { onSuccess: onClose });
  };

  const textField = (name: (typeof FIELDS)[number], label: string, dir?: 'rtl') => (
    <FormField
      control={form.control}
      name={name as 'bannerTitleEn'}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <FormControl>
            <Input {...field} dir={dir} />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-base">
            {discount ? 'Edit global discount' : 'New global discount'}
          </DialogTitle>
          <DialogDescription className="text-xs">
            A percent off every voucher in {countryName} for a period. Only one global discount per
            country per period. Coupons can&apos;t be used while it runs.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form noValidate className="space-y-4 text-xs" onSubmit={form.handleSubmit(submit)}>
            {locked && (
              <p className="bg-muted text-muted-foreground flex items-start gap-2 rounded-md p-2">
                <Lock className="mt-0.5 size-3.5 shrink-0" aria-hidden />
                Orders already used this sale, so its percent and cap are locked. You can still
                change its dates and banner.
              </p>
            )}
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="percentOff"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Percent off</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        inputMode="decimal"
                        min={1}
                        max={90}
                        step="0.01"
                        disabled={locked}
                        value={numberValue(field.value)}
                        onChange={(event) =>
                          field.onChange(
                            event.target.value === '' ? Number.NaN : Number(event.target.value),
                          )
                        }
                      />
                    </FormControl>
                    <FormDescription>1–90% off every voucher.</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="maxDiscountAmount"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Maximum per item</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        inputMode="decimal"
                        min={0}
                        step="0.01"
                        disabled={locked}
                        value={numberValue(field.value)}
                        onChange={(event) =>
                          field.onChange(
                            event.target.value === '' ? null : Number(event.target.value),
                          )
                        }
                      />
                    </FormControl>
                    <FormDescription>
                      {currency ?? 'Settlement currency'}. Empty = no cap.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="startsAt"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Starts</FormLabel>
                    <FormControl>
                      <Input type="datetime-local" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="endsAt"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Ends</FormLabel>
                    <FormControl>
                      <Input type="datetime-local" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="border-border space-y-3 rounded-md border p-3">
              <FormField
                control={form.control}
                name="bannerEnabled"
                render={({ field }) => (
                  <FormItem className="flex items-center gap-2 space-y-0">
                    <FormControl>
                      <Switch checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                    <FormLabel className="text-xs">Show the banner on the storefront</FormLabel>
                  </FormItem>
                )}
              />
              <p className="text-muted-foreground">
                The banner shows only while the sale is running, and can be switched on or off at
                any time.
              </p>
              <div className="grid gap-3 sm:grid-cols-2">
                {textField('bannerTitleEn', 'Title (English)')}
                {textField('bannerTitleAr', 'Title (Arabic)', 'rtl')}
                {textField('bannerTextEn', 'Text (English)')}
                {textField('bannerTextAr', 'Text (Arabic)', 'rtl')}
              </div>
              <BannerPreview
                titleEn={preview.bannerTitleEn ?? ''}
                textEn={preview.bannerTextEn ?? ''}
                titleAr={preview.bannerTitleAr ?? ''}
                textAr={preview.bannerTextAr ?? ''}
              />
            </div>

            {textField('description', 'Internal note')}

            {discount?.marginWarning && (
              <p role="status" className="text-status-warning">
                {discount.marginWarning}
              </p>
            )}
            {formError && (
              <p role="alert" className="text-destructive">
                {formError}
              </p>
            )}
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={mutation.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={mutation.isPending}>
                {mutation.isPending && <Loader2 className="size-3.5 animate-spin" aria-hidden />}
                {discount ? 'Save' : 'Create global discount'}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
